import React from 'react';
import { useTranslation } from 'react-i18next';
import { Overlay } from 'Core/components/Layout';
import { SecondaryOutlineButton } from 'Core/components/Button';

const FinancingModal = ({ isOpen, onClose, url, children }) => {
  const { t } = useTranslation();
  const [showIframe, setShowIframe] = React.useState(true);
  const [iframeError, setIframeError] = React.useState(false);

  if (!isOpen) return null;

  const handleIframeError = () => {
    setIframeError(true);
    setShowIframe(false);
  };

  const navigateToFinancing = () => {
    // Store current URL to return to later
    sessionStorage.setItem('returnUrl', window.location.href);
    sessionStorage.setItem('isTeleaudiologyFlow', 'true');
    // Navigate to financing URL in same tab
    window.location.href = url;
  };

  return (
    <Overlay className="z-50">
      <div className="bg-white w-[95vw] h-[95vh] max-w-[95vw] max-h-[95vh] rounded-lg shadow-2xl flex flex-col relative">
        {/* Header with close button */}
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">
            {t('financing.modal.title', 'Financing Application')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200 text-2xl font-bold text-gray-600 hover:text-gray-800"
            data-testid="modal-close-x"
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        {/* Main content area */}
        <div className="flex-1 p-4">
          {url ? (
            <>
              {showIframe && !iframeError ? (
                // onError is for load failures, not user interaction; iframe has no interactive role
                // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
                <iframe
                  src={url}
                  className="w-full h-full border-0 rounded"
                  title="Financing Application"
                  onError={handleIframeError}
                  sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-top-navigation"
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center space-y-6">
                  <div className="text-lg text-gray-700">
                    <p>
                      {t(
                        'financing.modal.navigateMessage',
                        'You will be redirected to the financing application. You can return to this page when finished.'
                      )}
                    </p>
                  </div>
                  <button
                    onClick={navigateToFinancing}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
                  >
                    {t(
                      'financing.modal.continueToApplication',
                      'Continue to Financing Application'
                    )}
                  </button>
                  <div className="text-sm text-gray-500">
                    <p>
                      {t(
                        'financing.modal.navigationNote',
                        'This page will be replaced with the financing application. Your progress will be saved.'
                      )}
                    </p>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-gray-500">
                <p>{t('financing.modal.noUrl', 'No financing URL available')}</p>
              </div>
            </div>
          )}
          {children}
        </div>

        {/* Footer with close button */}
        <div className="flex justify-end p-4 border-t border-gray-200">
          <SecondaryOutlineButton
            onClick={onClose}
            data-testid="modal-close-button"
            className="px-6 py-2"
          >
            {t('common.close', 'Close')}
          </SecondaryOutlineButton>
        </div>
      </div>
    </Overlay>
  );
};

export default FinancingModal;
